/**
 * useAllArbitrageConfigs Hook
 *
 * Loads arbitrage configurations for all projects via React Query.
 * Avoids persistent IndexedDB caching; relies on in-memory query cache only.
 *
 * Consumers can explicitly refresh a single project's data (e.g. when user expands a project).
 */

import { useQueries, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import type { RootStore } from 'store';

import { ARBITRAGE_CONFIGS_RELOAD } from 'shared/consts/reload';
import { Bus } from 'tools';
import type { IArbitrageConfig } from 'types/arbitrage';

import { ARBITRAGE_QUERY_KEYS, getArbitrageConfigs } from '../api';

export interface EnrichedArbitrageConfig extends IArbitrageConfig {
  projectName: string;
  isArchived: boolean;
}

type UseAllArbitrageConfigsResult = {
  arbitrages: EnrichedArbitrageConfig[];
  isLoading: boolean;
  isError: boolean;
  refreshProject: (projectId: number) => Promise<void>;
};

const DEFAULT_STALE_TIME_MS = 5 * 60 * 1000; // 5 minutes

export const useAllArbitrageConfigs = (enabled = true): UseAllArbitrageConfigsResult => {
  const projects = useSelector((state: RootStore) => state.projects.projects);
  const archivedProjects = useSelector((state: RootStore) => state.projects.archivedProjects);

  const queryClient = useQueryClient();

  // Only proceed if projects have been loaded (not null) AND fetching is enabled
  const projectsLoaded = projects !== null;

  const allProjects = useMemo(() => {
    if (!projectsLoaded) return [];
    const active = projects.map(p => ({ ...p, isArchived: false }));
    const archived = (archivedProjects ?? []).map(p => ({ ...p, isArchived: true }));
    return [...active, ...archived];
  }, [projects, archivedProjects, projectsLoaded]);

  const queries = useQueries({
    queries:
      enabled && projectsLoaded
        ? allProjects.map(project => ({
            queryKey: ARBITRAGE_QUERY_KEYS.listByProject(project.id),
            queryFn: async ({ signal }) => {
              const response = await getArbitrageConfigs({
                projectId: project.id,
                signal,
              });

              if (!response.isSuccess || !response.data) {
                throw new Error(response.errorMessage || 'Failed to fetch arbitrage configs');
              }

              return response.data;
            },
            staleTime: DEFAULT_STALE_TIME_MS,
          }))
        : [],
  });

  const refreshProject = useCallback(
    async (projectId: number): Promise<void> => {
      if (!enabled) return;

      await queryClient.invalidateQueries({
        queryKey: ARBITRAGE_QUERY_KEYS.listByProject(projectId),
      });
      await queryClient.refetchQueries({
        queryKey: ARBITRAGE_QUERY_KEYS.listByProject(projectId),
      });
    },
    [enabled, queryClient],
  );

  useEffect(() => {
    if (!enabled) return;

    const handleReload = async (payload?: unknown) => {
      if (!projectsLoaded) return;

      const projectId = typeof payload === 'number' ? payload : null;

      if (projectId === null) {
        await queryClient.invalidateQueries({ queryKey: ARBITRAGE_QUERY_KEYS.all });
        await queryClient.refetchQueries({ queryKey: ARBITRAGE_QUERY_KEYS.all });
        return;
      }

      await refreshProject(projectId);
    };

    Bus.on(ARBITRAGE_CONFIGS_RELOAD, handleReload);
    return () => {
      Bus.off(ARBITRAGE_CONFIGS_RELOAD, handleReload);
    };
  }, [enabled, projectsLoaded, queryClient, refreshProject]);

  const arbitrages = useMemo(() => {
    const result: EnrichedArbitrageConfig[] = [];

    queries.forEach((query, index) => {
      const items = query.data;
      if (!items) return;

      const project = allProjects[index];
      if (!project) return;

      for (const config of items) {
        result.push({
          ...config,
          projectName: project.name,
          isArchived: project.isArchived,
        });
      }
    });

    return result;
  }, [allProjects, queries]);

  const isLoading = enabled && (!projectsLoaded || queries.some(query => query.isLoading));
  const isError = queries.some(query => query.isError);

  return { arbitrages, isLoading, isError, refreshProject };
};
